import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { loginUser, loginWithGoogle, logoutUser } from "./store/authSlice";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { ROUTES } from "@/routes/routeConfig";
import { IMAGES } from "@/constants/images";
import { LoginIconSprite, type MascotMode } from "./LoginArt";
import { StoryMascot } from "./LoginStory";

const DASHBOARD_BY_SCOPE: Record<string, string> = {
  SUPER_ADMIN: ROUTES.SUPER_ADMIN.DASHBOARD,
  ORG: ROUTES.ORG_ADMIN.DASHBOARD,
  CAMPUS: ROUTES.CAMPUS_ADMIN.DASHBOARD,
  INSTITUTION: ROUTES.INSTITUTION_ADMIN.DASHBOARD,
  STUDENT_PARENT: ROUTES.STUDENT_PARENT.DASHBOARD,
  TEACHER: ROUTES.TEACHER_PORTAL.DASHBOARD,
  STUDENT: ROUTES.STUDENT_PORTAL.DASHBOARD,
  PARENT: ROUTES.STUDENT_PORTAL.DASHBOARD,
  ACCOUNTANT: ROUTES.STAFF_PORTAL.DASHBOARD,
  LIBRARIAN: ROUTES.STAFF_PORTAL.DASHBOARD,
  DYNAMIC: ROUTES.STAFF_PORTAL.DASHBOARD,
};

/** Rotating hero copy on the left panel. Each slide also highlights one of the floating module chips. */
const SLIDES = [
  { title: "Everything your school needs, in one place.", text: "Streamlined administration, real-time academics, and portals built for every role.", chip: 0 },
  { title: "Attendance, fees and exams, always in sync.", text: "Teachers, students and parents see the same up-to-date information.", chip: 1 },
  { title: "A panel made for every role.", text: "Admins, teachers, students, parents and staff each get their own workspace.", chip: 2 },
];




const CSS = `
@media (prefers-reduced-motion: no-preference) {
  @keyframes lg-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-9px) } }
  @keyframes lg-bob { 0%,100% { transform: translateY(0) rotate(-0.6deg) } 50% { transform: translateY(-8px) rotate(0.6deg) } }
  @keyframes lg-rise { from { opacity: 0; transform: translateY(18px) scale(.985) } to { opacity: 1; transform: none } }
  @keyframes lg-shake { 0%,100% { transform: translateX(0) } 20% { transform: translateX(-7px) } 40% { transform: translateX(6px) } 60% { transform: translateX(-4px) } 80% { transform: translateX(3px) } }
  @keyframes lg-slide { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
  @keyframes lg-spin-slow { to { transform: rotate(360deg) } }
  .lg-float { animation: lg-float 5s ease-in-out infinite }
  .lg-bob { animation: lg-bob 4.5s ease-in-out infinite }
  .lg-rise { animation: lg-rise .6s cubic-bezier(.2,.8,.2,1) both }
  .lg-shake { animation: lg-shake .45s ease-in-out }
  .lg-slide { animation: lg-slide .5s ease both }
  .lg-orbit { animation: lg-spin-slow 60s linear infinite }
}
`;

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.auth);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [noPortalError, setNoPortalError] = useState("");
  const [slide, setSlide] = useState(0);
  const [shake, setShake] = useState(false);
  const [focused, setFocused] = useState<"email" | "password" | null>(null);
  const [errAck, setErrAck] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const formPanelRef = useRef<HTMLDivElement>(null);

  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("reason") === "session_expired";
  const tenantInactive = searchParams.get("reason") === "tenant_inactive";
  const googleSignInEnabled = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // The login screen is exactly one screen tall: stop the browser from scrolling the page behind it.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = { h: html.style.overflow, b: body.style.overflow, hh: html.style.height, bh: body.style.height };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.height = "100%";
    return () => { html.style.overflow = prev.h; body.style.overflow = prev.b; html.style.height = prev.hh; body.style.height = prev.bh; };
  }, []);

  // Auto-advance the hero copy; pauses while the tab is hidden.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!document.hidden) setSlide((s) => (s + 1) % SLIDES.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, []);

  // A failed login shakes the card once.
  useEffect(() => {
    if (!error) return;
    setErrAck(false);
    setShake(true);
    const id = window.setTimeout(() => setShake(false), 500);
    return () => window.clearTimeout(id);
  }, [error]);

  // Enter the app for the given scope, or explain clearly when there's no
  // portal built for it yet (DYNAMIC roles currently have none) —
  // rather than silently bouncing back to this same page with no explanation.
  function enterForScope(scope: string) {
    if (scope === "DRIVER") {
      dispatch(logoutUser());
      setNoPortalError("Drivers sign in through the mobile app. Please use the app to continue.");
      return;
    }
    const destination = DASHBOARD_BY_SCOPE[scope];
    if (!destination) {
      setNoPortalError(
        "Login succeeded, but there's no portal for this account type yet. Please contact your administrator."
      );
      return;
    }
    navigate(destination, { replace: true });
  }

  // Shared by both password login and Google login below — same
  // first-login/portal-routing rules apply regardless of how identity was proven.
  function handleLoginResult(user: { role: { scope: string }; isFirstLogin: boolean }) {
    const scope = user.role.scope;
    enterForScope(scope);
  }

  // One browser holds one signed-in account: its session lives in localStorage, shared by every tab. A login page that was already open when
  // another tab signed in must say so and offer the dashboard, not let a second account overwrite the first.
  function readExisting(): { name: string; scope: string } | null {
    if (!localStorage.getItem("token")) return null;
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      if (!u || u.isFirstLogin) return null;
      return { name: String(u.name || u.email || "your account"), scope: u.role?.scope ?? "" };
    } catch {
      return null;
    }
  }
  const [existing, setExisting] = useState(readExisting);
  useEffect(() => {
    const refresh = () => setExisting(readExisting());
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    return () => { window.removeEventListener("storage", refresh); window.removeEventListener("focus", refresh); };
  }, []);
  async function signOutExisting() {
    await dispatch(logoutUser());
    setExisting(null);
  }
  // A full load, so this tab reads the session another tab created.
  function goToDashboard() {
    const target = existing ? DASHBOARD_BY_SCOPE[existing.scope] : undefined;
    window.location.assign(target ?? ROUTES.LOGIN);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setNoPortalError("");
    if (readExisting()) { setExisting(readExisting()); return; }
    const result = await dispatch(loginUser({ email, password }));
    if (loginUser.fulfilled.match(result)) {
      handleLoginResult(result.payload.user);
    }
  }

  async function handleGoogleCredential(idToken: string) {
    setNoPortalError("");
    if (readExisting()) { setExisting(readExisting()); return; }
    const result = await dispatch(loginWithGoogle(idToken));
    if (loginWithGoogle.fulfilled.match(result)) {
      handleLoginResult(result.payload.user);
    }
  }

  const current = SLIDES[slide];
  // What the mascot is doing: watching you type, shutting his eyes for the password, peeking when it is shown, etc.
  const mascotMode: MascotMode = loading
    ? "loading"
    : error && !errAck
      ? "error"
      : focused === "password"
        ? showPassword ? "peek" : "password"
        : focused === "email" ? "email" : "idle";

  return (
    <div className="font-satoshi relative flex h-dvh w-full overflow-hidden overscroll-none bg-gradient-to-br from-[#FFF7F0] via-[#FDF6EF] to-[#F3F4F6] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-11 sm:px-6">
      <style>{CSS}</style>
      <LoginIconSprite />

      {/* soft background blobs */}
      <div aria-hidden className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-orange-300/30 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-24 h-[30rem] w-[30rem] rounded-full bg-amber-200/40 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.35]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(249,115,22,.18) 1px, transparent 0)", backgroundSize: "26px 26px" }} />

      

      <div ref={cardRef} className={`lg-rise relative m-auto grid w-full max-w-md rounded-2xl sm:max-w-lg sm:rounded-3xl md:max-w-xl lg:max-w-5xl bg-white shadow-[0_30px_80px_-30px_rgba(194,65,12,0.35),0_8px_24px_-12px_rgba(15,23,42,0.15)] lg:grid-cols-2 ${shake ? "lg-shake" : ""}`}>
        {/* ===== Tablet banner: stands in for the hero panel on portrait tablets ===== */}
        <div className="relative m-3 mb-0 hidden overflow-hidden rounded-[1.2rem] bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 px-6 py-5 text-white md:[@media(min-height:800px)]:block lg:!hidden">
          <div aria-hidden className="pointer-events-none absolute -right-8 -top-10 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
          <Link
  to={ROUTES.LANDING}
  className="group inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition-colors hover:bg-white/25"
>
  <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
  Back to home
</Link>
          <div key={slide} className="lg-slide relative mt-3">
            <h2 className="text-xl font-extrabold leading-snug tracking-tight">{current.title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-white/85">{current.text}</p>
          </div>
        </div>

        {/* ===== Left panel — hero ===== */}
        <div
          ref={heroRef}
          className="relative m-3 hidden min-h-[600px] [@media(max-height:820px)]:min-h-[520px] [@media(max-height:700px)]:min-h-[440px] [@media(max-height:640px)]:min-h-[380px] overflow-hidden rounded-[1.4rem] bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white lg:block"
        >
          {/* patterns */}
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "34px 34px", maskImage: "radial-gradient(ellipse at 50% 60%, #000 20%, transparent 75%)", WebkitMaskImage: "radial-gradient(ellipse at 50% 60%, #000 20%, transparent 75%)" }} />
          <div aria-hidden className="lg-orbit pointer-events-none absolute left-1/2 top-[58%] h-[26rem] w-[26rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20" />
          <div aria-hidden className="pointer-events-none absolute left-1/2 top-[58%] h-[19rem] w-[19rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15" />
          <div aria-hidden className="pointer-events-none absolute left-1/2 top-[62%] h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/20 blur-3xl" />
          <span aria-hidden className="lg-float absolute right-10 top-14 text-2xl text-white/70">✦</span>
          <span aria-hidden className="lg-float absolute left-1/3 top-[38%] text-sm text-white/50" style={{ animationDelay: "-2s" }}>✦</span>

          {/* copy (rotates) */}
          <div className="relative z-10 px-8 pt-8 [@media(max-height:700px)]:!pt-5">
            <Link
  to={ROUTES.LANDING}
  className="group inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/25"
>
  <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
  Back to home
</Link>
            <div key={slide} className="lg-slide mt-5 min-h-[10.5rem]">
              <h2 className="max-w-md text-3xl font-extrabold leading-tight tracking-tight xl:text-4xl">{current.title}</h2>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/85">{current.text}</p>
            </div>
          </div>

          {/* carousel dots */}
          <div className="absolute inset-x-0 bottom-4 z-20 flex justify-center gap-1.5">
            {SLIDES.map((_, i) => (
              <button key={i} type="button" aria-label={`Show message ${i + 1}`} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all duration-300 ${slide === i ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`} />
            ))}
          </div>
        </div>

        {/* ===== Right panel — form ===== */}
        <div ref={formPanelRef} className="relative flex flex-col justify-center px-5 py-8 sm:px-10 md:px-14 [@media(max-height:820px)]:py-5 [@media(max-height:700px)]:py-3 [@media(max-height:640px)]:!py-2 [@media(max-height:520px)]:!px-4 [@media(max-height:520px)]:!py-1">
          <div className="mx-auto w-full max-w-sm">
            <div className="mb-6 sm:mb-7 [@media(max-height:820px)]:!mb-3 [@media(max-height:640px)]:!mb-2">
              <div className="flex justify-center">
                <img src={IMAGES.eduLogo} alt="EduManage+ logo" className="mb-4 h-20 w-auto object-contain sm:h-24 [@media(max-height:820px)]:!mb-2 [@media(max-height:820px)]:!h-[4.5rem] [@media(max-height:640px)]:!hidden" />
              </div>
              <h1 className="mb-1 text-center text-2xl font-extrabold text-orange-900 sm:text-[1.7rem] [@media(max-height:820px)]:!text-xl [@media(max-height:520px)]:!text-lg">
                Welcome back <span className="inline-block">👋</span>
              </h1>
              <p className="text-center text-xs font-medium text-slate-400 sm:text-sm [@media(max-height:640px)]:!hidden">Sign in to continue to your school panel</p>
            </div>

            {sessionExpired && !error && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700">
                Your session expired. Please log in again.
              </div>
            )}

            {tenantInactive && !error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                You were signed out because your institution is no longer active. Please contact your administrator.
              </div>
            )}

            {noPortalError && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700">
                {noPortalError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3 [@media(max-height:520px)]:space-y-1.5">
              <div>
                <label className="text-sm font-bold text-slate-800 mb-1.5 block [@media(max-height:520px)]:!mb-0.5">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-orange-500" />
                  <Input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrAck(true); }}
                    onFocus={() => setFocused("email")}
                    onBlur={() => setFocused((f) => (f === "email" ? null : f))}
                    placeholder="you@school.edu"
                    required
                    autoFocus
                    disabled={loading}
                    className="h-11 rounded-xl border-orange-200 bg-orange-50/40 pl-10 transition-shadow focus-visible:ring-orange-400 focus-visible:shadow-[0_0_0_4px_rgba(251,146,60,0.15)] sm:h-12 [@media(max-height:820px)]:!h-10 [@media(max-height:520px)]:!h-9"
                  />
                </div>
              </div>

              <div className="group">
                <label htmlFor="login-password" className="mb-1.5 block text-sm font-bold text-slate-800 transition-colors group-focus-within:text-orange-600 [@media(max-height:520px)]:!mb-0.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-orange-500" />
                  <Input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrAck(true); }}
                    onFocus={() => setFocused("password")}
                    onBlur={() => setFocused((f) => (f === "password" ? null : f))}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                    className="h-11 rounded-xl border-orange-200 bg-orange-50/40 pl-10 pr-11 transition-shadow focus-visible:ring-orange-400 focus-visible:shadow-[0_0_0_4px_rgba(251,146,60,0.15)] sm:h-12 [@media(max-height:820px)]:!h-10 [@media(max-height:520px)]:!h-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-orange-100/60 hover:text-orange-600"
                  >
                    <span className="relative h-4 w-4">
                      <Eye className={`absolute inset-0 h-4 w-4 transition-all duration-200 ${showPassword ? "rotate-90 scale-50 opacity-0" : "opacity-100"}`} />
                      <EyeOff className={`absolute inset-0 h-4 w-4 transition-all duration-200 ${showPassword ? "opacity-100" : "-rotate-90 scale-50 opacity-0"}`} />
                    </span>
                  </button>
                </div>
                <p className="mt-1.5 text-right text-xs text-slate-400 [@media(max-height:640px)]:!hidden">
                  Forgot your password? Contact your administrator to reset it.
                </p>
                {error && (
                  <p role="alert" className="mt-1.5 text-sm text-destructive">{error}</p>
                )}
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                disabled={loading}
                className="group/btn h-11 w-full rounded-xl !bg-gradient-to-r !from-orange-400 !to-orange-600 font-semibold !text-white shadow-lg shadow-orange-300/50 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-orange-300/60 hover:!opacity-95 active:translate-y-0 active:scale-[0.99] sm:h-12 [@media(max-height:820px)]:!h-10 [@media(max-height:520px)]:!h-9"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Logging in...</span>
                ) : (
                  <span className="inline-flex items-center gap-2">Log In <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" /></span>
                )}
              </Button>
            </form>

            {googleSignInEnabled && (
              <>
                <div className="my-5 flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs font-medium text-slate-400">OR</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
                <GoogleSignInButton onCredential={handleGoogleCredential} disabled={loading} />
              </>
            )}

            <p className="mt-5 text-center text-sm text-slate-500 sm:mt-6 [@media(max-height:820px)]:!mt-3 [@media(max-height:520px)]:!mt-1.5 [@media(max-height:520px)]:!text-xs">
              Don't have an account?{" "}
              <a href="#" className="font-semibold text-orange-500 hover:text-orange-600 hover:underline">
                Contact Support Team
              </a>
            </p>
          </div>
        </div>

        {/* walking mascot (desktop): crosses the orange panel, waves, then stands big at its right edge */}
        <StoryMascot mode={mascotMode} cardRef={cardRef} leftRef={heroRef} rightRef={formPanelRef} />
      </div>
    </div>
  );
}